import { z } from 'zod';
import { findItemDefinition, ITEM_DEFINITIONS, type ItemDefinition } from './definitions';

/**
 * The eight drawers a player sorts the marketplace into.
 *
 * ## Not the same list as `ItemCategory`, on purpose
 *
 * `ItemCategory` is what a definition IS to the game engine - six values, one
 * of which, `consumable`, covers two things a player would never look for in
 * the same place: a Ração de Combate and a Tech Paint Bucket. One feeds a pet
 * and the other repaints it. So `consumable` splits here, and only here.
 *
 * ## Never guessed from an id
 *
 * The split reads `definition.metadata.kind`, which is a typed discriminated
 * union the catalog already carries. Nothing in this file - and nothing in any
 * component - looks at a prefix, a substring or a name. `sphere_energy_*`
 * happens to be a readable id today; a category that depended on that would
 * break the first time content was renamed, silently and in the player's face.
 *
 * ## One place, four screens
 *
 * The buy grid, the sell list, "meus anúncios" and the history all ask here,
 * and the SERVER resolves a category to the definition ids it contains - the
 * same shape the name search already uses, so the two intersect cleanly and
 * neither is ever a pattern built from client input.
 */

/** `all` is a real member: it is what the tab bar opens on. */
export const MARKET_CATEGORIES = [
  'all',
  'ships',
  'cannons',
  'artifacts',
  'equipment',
  'pets',
  'consumables',
  'customization',
  'skins',
] as const;
export type MarketCategory = (typeof MARKET_CATEGORIES)[number];
export const marketCategorySchema = z.enum(MARKET_CATEGORIES);

/** What each drawer is called, in the language the game speaks. */
export const MARKET_CATEGORY_LABELS: Readonly<Record<MarketCategory, string>> = Object.freeze({
  all: 'TODOS',
  ships: 'NAVES',
  cannons: 'CANHÕES',
  artifacts: 'ARTEFATOS',
  equipment: 'EQUIPAMENTOS',
  pets: 'PETS',
  consumables: 'CONSUMÍVEIS',
  customization: 'PERSONALIZAÇÃO',
  skins: 'SKINS',
});

/**
 * Which drawer one definition belongs to.
 *
 * `all` is never returned: it is a filter, not a home. A definition this does
 * not recognise lands in `consumables` rather than vanishing from every tab -
 * an item nobody can find is worse than an item in the wrong drawer, and the
 * catalog-wide test below makes the wrong drawer a failing build anyway.
 */
export function marketCategoryOf(definitionId: string): Exclude<MarketCategory, 'all'> {
  const definition = findItemDefinition(definitionId);
  if (!definition) {
    return 'consumables';
  }
  switch (definition.category) {
    case 'ship':
      return 'ships';
    case 'cannon':
      return 'cannons';
    case 'artifact':
      return 'artifacts';
    case 'equipment':
      return 'equipment';
    case 'pet':
      return 'pets';
    case 'skin':
      return 'skins';
    default:
      break;
  }
  // The only split. An Energy Sphere and a Tech Paint Bucket change how a pet
  // LOOKS; a food changes what it does.
  const kind = definition.metadata?.kind;
  if (kind === 'pet_energy_sphere' || kind === 'pet_paint_bucket') {
    return 'customization';
  }
  return 'consumables';
}

/** True when a definition belongs in the drawer currently open. */
export function isInMarketCategory(definitionId: string, category: MarketCategory): boolean {
  return category === 'all' || marketCategoryOf(definitionId) === category;
}

/**
 * Every definition id in one drawer, or `null` for "no filter".
 *
 * `null` rather than the whole catalog, so a query can leave the condition out
 * entirely instead of listing eighty ids to mean "everything" - and so it
 * matches how `definitionIdsMatchingName` says the same thing.
 */
export function definitionIdsInMarketCategory(category: MarketCategory): readonly string[] | null {
  if (category === 'all') {
    return null;
  }
  return ITEM_DEFINITIONS.filter((definition) => marketCategoryOf(definition.id) === category).map(
    (definition) => definition.id,
  );
}

/** How many of a player's own items fall in each drawer. For the tab labels. */
export function countByMarketCategory(
  definitionIds: readonly string[],
): Readonly<Record<MarketCategory, number>> {
  const counts = Object.fromEntries(MARKET_CATEGORIES.map((category) => [category, 0])) as Record<
    MarketCategory,
    number
  >;
  for (const definitionId of definitionIds) {
    counts.all += 1;
    counts[marketCategoryOf(definitionId)] += 1;
  }
  return counts;
}

/** Every definition that would land in the fallback drawer. Used by a test. */
export function definitionsWithoutMarketCategory(
  definitions: readonly ItemDefinition[],
): readonly string[] {
  return definitions
    .filter(
      (definition) =>
        definition.category === 'consumable' &&
        definition.metadata?.kind !== 'pet_food' &&
        definition.metadata?.kind !== 'pet_energy_sphere' &&
        definition.metadata?.kind !== 'pet_paint_bucket' &&
        // The Quantum Stabiliser is a consumable and CONSUMÍVEIS is where a
        // player would look for it, so it lands in the fallback drawer on
        // purpose rather than by omission.
        definition.metadata?.kind !== 'equipment_stabilizer',
    )
    .map((definition) => definition.id);
}
